// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyBarTab = { key: string; label: string };

type Props = {
    /** The section's own views, drawn as the frame draws them: at the start, underlined when active. */
    tabs?: TyBarTab[];
    activeTab?: string;
    onTab?: (key: string) => void;
    addLabel?: string;
    onAdd?: () => void;
    /** The fields the filter mode swaps the bar for. Passing it is what reveals the filter button. */
    filterContent?: ReactNode;
    isFilterOpen?: boolean;
    onFilterOpen?: () => void;
    onFilterApply?: () => void;
    onFilterClear?: () => void;
    onFilterCancel?: () => void;
    hasFilters?: boolean;
    children?: ReactNode;
    after?: ReactNode;
};

/**
 * The gold bar above a section's content, and the one place a section's views and actions live.
 *
 * It has two states. Normally it is the frame's bar: tabs at the start, then a separator that fills
 * the space, then the filter button and the primary action at the far end. Pressing the filter button
 * replaces the whole bar with the section's filter fields — the bar is already the widest strip on the
 * screen, so a panel would only cover the results the person is filtering.
 */
export default function ActionsBar({
    tabs,
    activeTab,
    onTab,
    addLabel,
    onAdd,
    filterContent,
    isFilterOpen,
    onFilterOpen,
    onFilterApply,
    onFilterClear,
    onFilterCancel,
    hasFilters,
    children,
    after,
}: Props) {
    const t = useTranslation();
    const hasFilter = !!filterContent && !!onFilterOpen;
    /**
     * **Tabs are what shrink the add button to a square.** A bar with tabs already spends its start
     * on words somebody has to read to know where they are, and a labelled button at the far end is
     * a second thing competing for that first glance — the plus alone says the same thing. A bar
     * with no tabs has nothing else in it, so there the button keeps its label and earns the room.
     */
    const hasTabs = !!tabs && tabs.length > 0;

    if (hasFilter && isFilterOpen) {
        return (
            <div className="actions-bar actions-bar--filtering">
                <div className="actions-bar__fields">{filterContent}</div>

                <div className="actions-bar__ctas">
                    <button type="button" className="actions-bar__cta actions-bar__cta--clear" onClick={onFilterClear} title={t.common.clearFilters}>
                        <Icon name="clear" size={18} />
                    </button>
                    <button type="button" className="actions-bar__cta actions-bar__cta--cancel" onClick={onFilterCancel} title={t.common.cancel}>
                        <Icon name="close" size={16} />
                    </button>
                    <button type="button" className="actions-bar__cta actions-bar__cta--apply" onClick={onFilterApply} title={t.common.applyFilters}>
                        <Icon name="check" size={18} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="actions-bar">
            {hasTabs && (
                <nav className="actions-bar__tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`actions-bar__tab${tab.key === activeTab ? " actions-bar__tab--on" : ""}`}
                            onClick={() => onTab?.(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            )}

            {children}
            <span className="actions-bar__separator" />

            {/* The frame groups these two and pads them away from the border; the tabs sit flush. */}
            <div className="actions-bar__actions">
                {hasFilter && (
                    <button
                        type="button"
                        className={`actions-bar__filter${hasFilters ? " actions-bar__filter--on" : ""}`}
                        onClick={onFilterOpen}
                        aria-label={t.common.filters}
                    >
                        <Icon name="filter" size={24} />
                    </button>
                )}

                {onAdd && (
                    <button
                        type="button"
                        className={`actions-bar__add${hasTabs ? " actions-bar__add--compact" : ""}`}
                        onClick={onAdd}
                        aria-label={addLabel}
                        title={addLabel}
                    >
                        <Icon name="add" size={22} />
                        {!hasTabs && <span>{addLabel}</span>}
                    </button>
                )}

                {after}
            </div>
        </div>
    );
}
